<script setup>
// "Agendas que este agente pode usar" de um Cenario do Capitao (spec 011): resumo na lista de Cenarios e o seletor num
// dialogo. So aparece para Cenario que usa ferramenta de agenda, e so quando o catalogo carregou (administrador, modulo
// de Atendimentos ligado).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgendaScopesAPI from 'dashboard/api/agendaScopes';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AgendaScopeSelector from 'dashboard/routes/dashboard/kanban/agendas/AgendaScopeSelector.vue';
import {
  AGENT_TYPES,
  emptyScope,
  isEmptyScope,
  summaryNames,
  usesAgendaTools,
} from 'dashboard/routes/dashboard/kanban/agendas/agendaScopes';

const props = defineProps({
  scenarioId: { type: Number, required: true },
  tools: { type: Array, default: () => [] },
  scope: { type: Object, default: null },
  catalog: { type: Object, default: null },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const dialogRef = ref(null);
const draft = ref(emptyScope());
const saving = ref(false);

const visible = computed(
  () => !!props.catalog && usesAgendaTools(props.tools)
);

const summary = computed(() => {
  if (isEmptyScope(props.scope)) return t('AGENDA_SCOPES.ALL');
  const names = summaryNames(props.scope, props.catalog);
  if (props.scope.conversation_assignee) {
    names.unshift(t('AGENDA_SCOPES.ASSIGNEE_OPTION'));
  }
  return names.length
    ? t('AGENDA_SCOPES.SUMMARY', { names: names.join(', ') })
    : t('AGENDA_SCOPES.NONE_ACTIVE');
});

const openDialog = () => {
  draft.value = { ...emptyScope(), ...props.scope };
  dialogRef.value?.open();
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await AgendaScopesAPI.save({
      agentType: AGENT_TYPES.SCENARIO,
      agentId: props.scenarioId,
      practitionerIds: draft.value.practitioner_ids,
      userIds: draft.value.user_ids,
      conversationAssignee: draft.value.conversation_assignee,
    });
    emit('saved', { scenarioId: props.scenarioId, scope: data.data });
    useAlert(t('AGENDA_SCOPES.SAVED'));
    dialogRef.value?.close();
  } catch {
    useAlert(t('AGENDA_SCOPES.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div
    v-if="visible"
    class="flex items-center justify-between gap-3 px-4 py-2 -mt-1 border border-dashed rounded-xl border-n-weak"
    data-test="scenario-agenda-scope"
    :data-scenario-id="scenarioId"
  >
    <span class="text-sm text-n-slate-11" data-test="agenda-scope-summary">
      {{ summary }}
    </span>
    <Button
      :label="t('AGENDA_SCOPES.CHANGE')"
      slate
      xs
      ghost
      data-test="agenda-scope-change"
      @click="openDialog"
    />
    <Dialog
      ref="dialogRef"
      :title="t('AGENDA_SCOPES.TITLE')"
      :description="t('AGENDA_SCOPES.DESCRIPTION')"
      :confirm-button-label="t('AGENDA_SCOPES.SAVE')"
      :is-loading="saving"
      @confirm="save"
    >
      <AgendaScopeSelector v-model="draft" :catalog="catalog" />
    </Dialog>
  </div>
</template>
